<template>
  <div class="modal-card">
    <section class="modal-card-body p-lg">
      <Loader v-if="loading" :fullPage="false" />
      <div v-else class="container">
        <div class="columns is-multiline">
          <div class="column is-full">
            <span class="subtitle is-3">Определение значений</span>
          </div>
          <template v-if="sortedVariables.length >= 0">
            <div class="column is-full" v-for="(variable, index) of sortedVariables" :key="index">
              <TagSingleInput
                v-if="variable.type.batchVariable !== true"
                :label="variable.name"
                :level="variable.selectedLevel"
                :index="index"
                :data="series[index]"
                v-model="selected[index]"
                :field="'name'"
              ></TagSingleInput>
              <TagMultipleInput
                v-else
                :label="variable.name"
                :level="variable.selectedLevel"
                :index="index"
                :data="series[index]"
                v-model="selected[index]"
                :field="'name'"
              ></TagMultipleInput>
            </div>
          </template>

          <template v-if="sortedVariables.length === 0">
            <div class="column is-full">
              Шаблон не содержит переменных
            </div>
          </template>

          <div class="column is-full">
            <div class="buttons">
              <b-button @click="$parent.close()">Отмена</b-button>
              <b-button @click="generateDocument" type="is-primary">Продолжить</b-button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import api from '@/api/api';

export default {
  props: {
    templateId: {
      type: [Number, String]
    }
  },
  components: {
    TagMultipleInput: () => import(/* webpackChunkName: "TagMultipleInput" */ '@/components/shared/TagMultipleInput'),
    TagSingleInput: () => import(/* webpackChunkName: "TagSingleInput" */ '@/components/shared/TagSingleInput')
  },
  data() {
    return {
      loading: true,
      variables: [],
      fullSeries: [],
      series: [],
      selected: []
    };
  },

  mounted() {
    this.getTemplateVariables().then(async () => {
      if (this.variables.length === 0) {
        this.generateDocument();
      } else {
        await this.getSeries();
      }
    });
  },

  methods: {
    async getTemplateVariables() {
      if (this.$route.name !== 'template.view' && this.$route.name !== 'template.create') {
        await api.getTemplate(this.templateId).then(res => {
          this.variables = res.data.data.variables || [];
        });
      } else {
        this.variables = this.$store.state.template.data.variables || [];
      }
    },

    async getSeries() {
      this.loading = true;

      await api
        .getSeries()
        .then(res => {
          for (const variable of this.sortedVariables) {
            this.fullSeries = res.data;

            const serie = res.data.find(el => el.id == variable.data.indicatorId);

            const filteredByLevel = serie.data.map(dataObj => {
              const { level, ...rest } = dataObj;
              const selectable = dataObj.level == variable.selectedLevel ? true : false;
              return { level, selectable, ...rest };
            });

            this.series.push(filteredByLevel);
          }

          this.loading = false;
        })
        .catch(err => console.log(err));
    },

    validate() {
      if (this.sortedVariables.length !== this.selected.length) {
        return false;
      }

      for (const selected of this.selected) {
        if (selected === '' || selected === undefined) {
          return false;
        }
      }

      return true;
    },

    buildDataObject() {
      let data = [];

      const findSelectedId = (selectedId, selectedValue, iterationIndex, indexInSortedVariables, declarationType) => {
        if (declarationType === 'unsetted' && iterationIndex === indexInSortedVariables) {
          // назначаем значение переменная является равна той на шаге цикла которой мы находимся
          selectedId = selectedValue;
        } else if (declarationType === 'unsetted') {
          // если является открытой переменной с единичным значением
          selectedId = this.selected[indexInSortedVariables];
        }

        return selectedId;
      };

      const findInheritedId = dataObject => {
        return dataObject.map(el => {
          if (el.type.declarationType !== 'inherited') {
            return el;
          } else {
            // находим родительский элемент в переменных
            const parentElement = dataObject.find(variable => variable.internalId === el.parentId);
            const { selectedId, ...rest } = el;

            // ищем серию родительского показателя
            const parentElementSerie = this.fullSeries.find(serie => serie.id === parentElement.data.indicatorId);

            // ищем ID в сериях и возвращаем для родительского
            const parentIdInSerie = parentElementSerie.data.find(parent => parent.id === parentElement.selectedId);

            return {
              selectedId: parentIdInSerie.parent_item,
              ...rest
            };
          }
        });
      };

      // если присутствует множественная переменная
      if (this.isBatchProcess === true) {
        // Проходим по всем открытым переменным
        for (let indexOfVariable = 0; indexOfVariable < this.selected.length; indexOfVariable++) {
          // объявляем текущую переменную
          const selected = this.selected[indexOfVariable];

          // проверяем текущую переменную на массив
          if (Array.isArray(selected)) {
            // если массив (множественное значение, запускаем цикл по значениям)
            for (const selectedValue of selected) {
              // формируем объект из текущего значения переменной массива и остальных значений
              const dataObjectWithoutInherits = this.variables.map(variable => {
                // вытаскиваем ключи
                let { selectedId, internalId, ...rest } = variable;
                const { declarationType } = variable.type;

                // находим индекс переменной в отсортированном массиве
                const indexInSortedVariables = this.sortedVariables.findIndex(el => el.internalId === internalId);

                selectedId = findSelectedId(
                  selectedId,
                  selectedValue,
                  indexOfVariable,
                  indexInSortedVariables,
                  declarationType
                );

                // собираем объект обратно
                return { selectedId, internalId, ...rest };
              });

              // заполняем значения наследований
              const dataObjectWithInherited = findInheritedId(dataObjectWithoutInherits);

              // передаём получившийся объект в массив
              data.push(dataObjectWithInherited);
            }
          }
        }
      } else {
        // формируем объект из текущего значения переменной массива и остальных значений
        const dataObjectWithoutInherits = this.variables.map(variable => {
          // вытаскиваем ключи
          let { selectedId, internalId, ...rest } = variable;
          const { declarationType } = variable.type;

          // находим индекс переменной в отсортированном массиве
          const indexInSortedVariables = this.sortedVariables.findIndex(el => el.internalId === internalId);

          if (declarationType === 'unsetted') {
            // если является открытой переменной с единичным значением
            selectedId = this.selected[indexInSortedVariables];
          } else if (declarationType === 'inherited') {
            // если наследуем, то убираем временные ID
            selectedId = '';
          }

          // собираем объект обратно
          return { selectedId, internalId, ...rest };
        });

        // заполняем значения наследований
        const dataObjectWithInherited = findInheritedId(dataObjectWithoutInherits);

        // передаём получившийся объект в массив
        data.push(dataObjectWithInherited);
      }

      return data;
    },

    generateDocument() {
      if (!this.validate()) {
        this.toast({
          message: 'Ошибка, требуется заполнить все поля',
          type: 'is-danger'
        });

        return;
      }

      const data = this.buildDataObject();

      this.$emit('generateDocument', data);
      this.$parent.close();
    }
  },
  computed: {
    isBatchProcess() {
      return this.variables.some(variable => variable.type.batchVariable === true);
    },
    sortedVariables() {
      const variables = this.variables.filter(variable => {
        return variable.type.declarationType === 'unsetted';
      });

      return variables.sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }

        return 0;
      });
    }
  }
};
</script>
